/**
 * The tool rail. §6.3.
 *
 * Canva's rail: an icon and a word for each kind of thing, and a click opens
 * that thing's panel beside the rail - Design, Elements, Text, Uploads, Draw -
 * then the design's own Layers and the customer's Projects. The one that is
 * open wears the accent; nothing else on the rail does.
 */

import type { ReactNode } from 'react';
import { IconDesign, IconDraw, IconElements, IconLayers, IconProjects, IconText, IconUpload } from './icons';

/** Everything the rail can open. */
export type RailPanel = 'design' | 'elements' | 'text' | 'uploads' | 'draw' | 'layers' | 'projects';

/** Kept for the old name, which shortcuts and tests still say. */
export type Tool = RailPanel;

interface Props {
  open: string | null;
  onOpen: ( panel: RailPanel ) => void;
}

const TOOLS: Array<{ id: RailPanel; label: string; icon: ReactNode; key?: string }> = [
  { id: 'design', label: 'Design', icon: <IconDesign size={ 22 } /> },
  { id: 'elements', label: 'Elements', icon: <IconElements size={ 22 } /> },
  { id: 'text', label: 'Text', icon: <IconText size={ 22 } />, key: 'T' },
  { id: 'uploads', label: 'Uploads', icon: <IconUpload size={ 22 } />, key: 'I' },
  { id: 'draw', label: 'Draw', icon: <IconDraw size={ 22 } />, key: 'P' },
];

const MINE: Array<{ id: RailPanel; label: string; icon: ReactNode; key?: string }> = [
  { id: 'layers', label: 'Layers', icon: <IconLayers size={ 22 } />, key: 'L' },
  { id: 'projects', label: 'Projects', icon: <IconProjects size={ 22 } />, key: 'D' },
];

export function ToolRail( { open, onOpen }: Props ) {
  return (
    <nav className="tb-rail" aria-label="Tools">
      { TOOLS.map( ( entry ) => (
        <RailButton key={ entry.id } entry={ entry } active={ open === entry.id } onOpen={ onOpen } />
      ) ) }

      <div className="tb-rail__sep" role="presentation" />

      { MINE.map( ( entry ) => (
        <RailButton key={ entry.id } entry={ entry } active={ open === entry.id } onOpen={ onOpen } />
      ) ) }
    </nav>
  );
}

function RailButton( {
  entry,
  active,
  onOpen,
}: {
  entry: { id: RailPanel; label: string; icon: ReactNode; key?: string };
  active: boolean;
  onOpen: ( panel: RailPanel ) => void;
} ) {
  return (
    <button
      type="button"
      className="tb-rail__tool"
      data-rail={ entry.id }
      aria-pressed={ active }
      title={ entry.key ? `${ entry.label } (${ entry.key })` : entry.label }
      onClick={ () => onOpen( entry.id ) }
    >
      <span className="tb-rail__icon">{ entry.icon }</span>
      { entry.label }
    </button>
  );
}
